import { cn } from "@/lib/utils";

interface ChangeIndicatorProps {
  value: number;
  previousValue?: number;
  change?: number;
  changePercentage?: number;
  label: string;
  icon?: React.ReactNode;
  showPercentage?: boolean;
  className?: string;
}

export function ChangeIndicator({
  value,
  previousValue,
  change,
  changePercentage,
  label,
  icon,
  showPercentage = true,
  className,
}: ChangeIndicatorProps) {
  const hasChange = change !== undefined && change !== 0;
  const isPositive = change && change > 0;
  const isNegative = change && change < 0;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-neutral-600">{icon}</span>}
        <span className="text-xs font-medium text-neutral-600">{label}</span>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-neutral-950">
          {value.toLocaleString()}
        </span>

        {hasChange && (
          <div
            className={cn(
              "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
              isPositive && "bg-green-100 text-green-700",
              isNegative && "bg-red-100 text-red-700",
            )}
          >
            {isPositive ? (
              <i className="ri-arrow-up-line text-sm"></i>
            ) : (
              <i className="ri-arrow-down-line text-sm"></i>
            )}
            <span>
              {Math.abs(change).toLocaleString()}
              {showPercentage && changePercentage !== undefined && (
                <>
                  {" "}
                  ({changePercentage > 0 ? "+" : ""}
                  {changePercentage.toFixed(1)}%)
                </>
              )}
            </span>
          </div>
        )}
      </div>

      {previousValue !== undefined && hasChange && (
        <span className="text-xs text-neutral-500">
          from {previousValue.toLocaleString()}
        </span>
      )}
    </div>
  );
}

// Compact version for list views
export function ChangeIndicatorCompact({
  value,
  change,
  changePercentage,
  label,
  icon,
}: Omit<
  ChangeIndicatorProps,
  "previousValue" | "showPercentage" | "className"
>) {
  const hasChange = change !== undefined && change !== 0;
  const isPositive = change && change > 0;
  const isNegative = change && change < 0;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-neutral-600">{icon}</span>}
        <span className="text-xs font-medium text-neutral-600">{label}</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-neutral-950">
          {value.toLocaleString()}
        </span>

        {hasChange && (
          <span
            className={cn(
              "flex items-center gap-0.5 text-xs font-semibold",
              isPositive && "text-green-600",
              isNegative && "text-red-600",
            )}
          >
            {isPositive ? (
              <i className="ri-arrow-up-line"></i>
            ) : (
              <i className="ri-arrow-down-line"></i>
            )}
            {changePercentage !== undefined ? (
              <span>
                {changePercentage > 0 ? "+" : ""}
                {changePercentage.toFixed(0)}%
              </span>
            ) : (
              <span>
                {change > 0 ? "+" : ""}
                {change}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
